import { RedactionBanner } from "@/common/components/RedactionBanner";
import { ApiError } from "@/common/lib/api";
import { useEffect, useMemo, useState } from "react";
import { useAnimalHistory } from "../api/animalsApi";
import type { AnimalEvent } from "../types";
import { AnimalTimelineEvent } from "./AnimalTimelineEvent";

type Props = {
  accountId: string;
  animalId: string;
};

export function AnimalTimeline({ accountId, animalId }: Props) {
  const [cursor, setCursor] = useState<string | null>(null);
  const [accumulated, setAccumulated] = useState<AnimalEvent[]>([]);
  const [seenIds, setSeenIds] = useState<Set<string>>(() => new Set());

  const { data, isLoading, isFetching, isError, error, refetch } = useAnimalHistory(
    accountId,
    animalId,
    cursor,
  );

  // B48: el backend marca cada evento con `hasRedactedFields`; el frontend solo
  // cuenta. Para owner/admin/viewer siempre llega false (no se muestra banner).
  // Para operator viene true en eventos con campos contables redactados — sin
  // que el frontend tenga que conocer la lista de tipos contables.
  const redactedCount = useMemo(
    () => accumulated.filter((e) => e.hasRedactedFields).length,
    [accumulated],
  );

  useEffect(() => {
    if (!data) return;
    setAccumulated((prev) => {
      const next = [...prev];
      const seen = new Set(seenIds);
      let added = false;
      for (const event of data.events) {
        if (seen.has(event.id)) continue;
        seen.add(event.id);
        next.push(event);
        added = true;
      }
      if (added) setSeenIds(seen);
      return added ? next : prev;
    });
  }, [data, seenIds]);

  if (isLoading && accumulated.length === 0) {
    return <TimelineSkeleton />;
  }

  if (isError && accumulated.length === 0) {
    if (error instanceof ApiError && error.status === 404) {
      return (
        <p className="rounded-2xl border border-app-border bg-app-surface p-4 text-app-ink-3 text-sm">
          Animal no encontrado.
        </p>
      );
    }
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-app-border bg-app-surface p-6 text-center">
        <p className="text-app-ink-2 text-sm">No se pudo cargar la línea de tiempo.</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded-xl border border-app-border bg-app-surface-2 px-4 py-2 font-medium text-[13px] text-app-ink-1 hover:border-app-ink-3"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (accumulated.length === 0) {
    return (
      <div className="rounded-2xl border border-app-border bg-app-surface p-6 text-center">
        <p className="text-app-ink-2 text-sm">Este animal aún no tiene eventos registrados.</p>
      </div>
    );
  }

  const hasMore = data?.nextCursor !== null && data?.nextCursor !== undefined;

  return (
    <div>
      {redactedCount > 0 && (
        <div className="mb-3">
          <RedactionBanner count={redactedCount} />
        </div>
      )}
      <ol className="relative">
        {accumulated.map((event, idx) => (
          <AnimalTimelineEvent
            key={event.id}
            event={event}
            isLast={idx === accumulated.length - 1}
          />
        ))}
      </ol>

      {hasMore && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => setCursor(data?.nextCursor ?? null)}
            disabled={isFetching}
            className="rounded-xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-1 hover:bg-app-surface-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isFetching ? "Cargando…" : "Cargar más"}
          </button>
        </div>
      )}
    </div>
  );
}

function TimelineSkeleton() {
  return (
    <div className="space-y-3">
      {["a", "b", "c"].map((k) => (
        <div
          key={k}
          className="h-24 animate-pulse rounded-2xl border border-app-border bg-app-surface-2"
        />
      ))}
    </div>
  );
}
